import {useRef, useEffect, useMemo} from 'react';
import styled from 'styled-components';
import type {Ticket} from '../types/ticket';
import {TicketListCard} from './TicketCard.list';
import {TicketRow} from './TicketRow';
import {DEFAULT_VISIBLE_COLUMN_KEYS, LG_COLUMNS, type ColumnKey} from '../constants/listColumns';
import {useGroupCollapse} from '../hooks/useGroupCollapse';

interface Props {
	tickets: Ticket[];
	density?: 'comfortable' | 'dense';
	sortField?: string;
	sortOrder?: 'asc' | 'desc';
	onSort?: (field: string) => void;
	onAssigneeChange?: (ticketId: string, userId: string | null) => void;
	onDelete?: (ticketId: string) => void;
	selectedIds?: Set<string>;
	onToggleSelect?: (ticketId: string) => void;
	onToggleAll?: () => void;
	/**
	 * Which columns are currently visible at lg. When omitted, all columns
	 * are shown — matching the table's pre-toggle behavior so existing
	 * callers and tests don't need to be updated.
	 */
	visibleColumns?: ReadonlySet<ColumnKey>;
}

const STATUS_GROUP_ORDER = ['new', 'open', 'in_progress', 'waiting_on_customer', 'blocked', 'resolved', 'closed'] as const;

const STATUS_GROUP_LABELS: Record<string, string> = {
	new: 'New',
	open: 'Open',
	in_progress: 'In progress',
	waiting_on_customer: 'Waiting on customer',
	blocked: 'Blocked',
	resolved: 'Resolved',
	closed: 'Closed',
};

function groupByStatus(tickets: Ticket[]): Map<string, Ticket[]> {
	const groups = new Map<string, Ticket[]>();
	for (const status of STATUS_GROUP_ORDER) {
		const matching = tickets.filter(t => t.status === status);
		if (matching.length > 0) {
			groups.set(status, matching);
		}
	}
	return groups;
}

const ALL_VISIBLE: ReadonlySet<ColumnKey> = DEFAULT_VISIBLE_COLUMN_KEYS;

const Wrap = styled.div`
	margin-top: 16px;
`;
// Full column headers — visible at lg+ (1024px). Default visible so jsdom (no
// layout) still renders them for tests; hidden below lg in real browsers.
const LgHeader = styled.div`
	display: grid;
	align-items: center;
	gap: 12px;
	padding: 8px 16px;
	font: ${p => p.theme.ticketing.font.overline};
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: #94a3b8;
	border-bottom: 1px solid #f1f5f9;
	@media (max-width: 1023px) {
		display: none;
	}
`;
// Medium headers — md (768px) to lg.
const MdHeader = styled.div<{$hasSelection: boolean}>`
	display: grid;
	grid-template-columns: ${p => (p.$hasSelection ? '2rem 4rem 1fr 150px 140px 100px' : '4rem 1fr 150px 140px 100px')};
	align-items: center;
	gap: 12px;
	padding: 8px 16px;
	font: ${p => p.theme.ticketing.font.overline};
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: #94a3b8;
	border-bottom: 1px solid #f1f5f9;
	@media (max-width: 767px), (min-width: 1024px) {
		display: none;
	}
`;
const SelectCell = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
`;
const SelectAll = styled.input`
	height: 16px;
	width: 16px;
`;
const Group = styled.div`
	margin-bottom: 8px;
`;
const GroupHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 16px;
	background: ${p => p.theme.ticketing.color.bgSubtle};
	border-bottom: 1px solid #f1f5f9;
	cursor: pointer;
	user-select: none;
`;
const GroupLabel = styled.span`
	font: ${p => p.theme.ticketing.font.subheading1};
	letter-spacing: 0.15px;
	color: #334155;
`;
const GroupCount = styled.span`
	font: ${p => p.theme.ticketing.font.body3};
	color: ${p => p.theme.ticketing.color.textSecondary};
`;
const Chevron = styled.svg<{$collapsed: boolean}>`
	height: 16px;
	width: 16px;
	margin-left: 4px;
	color: currentColor;
	transition: transform 0.15s;
	transform: rotate(${p => (p.$collapsed ? '-90deg' : '0')});
`;
const MobileStack = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding: 8px;
	@media (min-width: 768px) {
		display: none;
	}
`;

export function TicketListTable({
	tickets,
	onAssigneeChange,
	onDelete,
	selectedIds,
	onToggleSelect,
	onToggleAll,
	visibleColumns,
}: Props) {
	const hasSelection = !!selectedIds && !!onToggleAll;
	const allSelected = hasSelection && tickets.length > 0 && tickets.every(t => selectedIds.has(t.id));
	const someSelected = hasSelection && tickets.some(t => selectedIds.has(t.id)) && !allSelected;

	const selectAllRef = useRef<HTMLInputElement>(null);
	useEffect(() => {
		if (selectAllRef.current) {
			selectAllRef.current.indeterminate = someSelected;
		}
	}, [someSelected]);

	const visible = visibleColumns ?? ALL_VISIBLE;
	const lgHeaders = useMemo(() => LG_COLUMNS.filter(c => visible.has(c.key)), [visible]);
	const lgTemplate = useMemo(() => {
		const cols = lgHeaders.map(c => c.width).join(' ');
		return hasSelection ? `2rem ${cols}` : cols;
	}, [lgHeaders, hasSelection]);

	const {isCollapsed, toggle} = useGroupCollapse();

	const groups = groupByStatus(tickets);

	return (
		<Wrap>
			<LgHeader data-testid="lg-header" style={{gridTemplateColumns: lgTemplate}}>
				{hasSelection && (
					<SelectCell>
						<SelectAll
							ref={selectAllRef}
							type="checkbox"
							checked={allSelected}
							onChange={onToggleAll}
							aria-label="Select all tickets"
						/>
					</SelectCell>
				)}
				{lgHeaders.map(c => (
					<span key={c.key}>{c.label}</span>
				))}
			</LgHeader>

			<MdHeader data-testid="md-header" $hasSelection={hasSelection}>
				{hasSelection && (
					<SelectCell>
						<SelectAll
							type="checkbox"
							checked={allSelected}
							onChange={onToggleAll}
							aria-label="Select all tickets"
						/>
					</SelectCell>
				)}
				<span>ID</span>
				<span>Subject</span>
				<span>Requester</span>
				<span>Assignee</span>
				<span>Priority</span>
			</MdHeader>

			{Array.from(groups.entries()).map(([status, groupTickets]) => {
				const collapsed = isCollapsed(status);
				const contentId = `group-content-${status}`;
				return (
					<Group key={status} data-testid={`group-${status}`}>
						<GroupHeader
							data-testid={`group-header-${status}`}
							role="button"
							tabIndex={0}
							aria-expanded={!collapsed}
							aria-controls={contentId}
							onClick={() => toggle(status)}
							onKeyDown={e => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									toggle(status);
								}
							}}
						>
							<GroupLabel data-testid="group-header-label">{STATUS_GROUP_LABELS[status] ?? status}</GroupLabel>
							<GroupCount data-testid="group-header-count">{groupTickets.length}</GroupCount>
							<Chevron
								$collapsed={collapsed}
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
								strokeWidth={2}
							>
								<path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
							</Chevron>
						</GroupHeader>

						<div id={contentId} data-testid={`group-content-${status}`} hidden={collapsed}>
							{groupTickets.map(ticket => (
								<TicketRow
									key={ticket.id}
									ticket={ticket}
									onAssigneeChange={onAssigneeChange}
									onDelete={onDelete}
									selected={selectedIds?.has(ticket.id)}
									onSelect={onToggleSelect}
									visibleColumns={visible}
								/>
							))}

							<MobileStack data-testid={`mobile-stack-${status}`}>
								{groupTickets.map(ticket => (
									<TicketListCard key={ticket.id} ticket={ticket} />
								))}
							</MobileStack>
						</div>
					</Group>
				);
			})}
		</Wrap>
	);
}
